import React from 'react';
import { isMobile } from '../../../utils/screen';
import {
	CONFIRMATION_AGENDAS, CONFIRMATION_ITEMS_AGENDA, CUSTOM_ABSTENTION_AGENDAS,
	CUSTOM_AGENDAS, DEFAULT_ITEMS_AGENDA, SHAREHOLDER_DATA
} from '../../utils/constants';
import { getText } from '../../utils/functions';
import { useIcons } from '../../utils/hooks';
import SimpleButton from '../basicComponents/SimpleButton';
import Spinner from '../basicComponents/Spinner';

function Summary({
	formData,
	portal,
	formType,
	translate,
	sending,
	onConfirm,
	setOpen,
	colors,

	/* OPTIONAL PROPS */
	classNameDIV = 'p-8 md:pt-9 md:pb-14 md:px-16 flex flex-col rounded-sm',
	styleDIV,
	classNameTITLE = 'md:text-xl self-center',
	styleSPAN,
	classNameDATA = 'space-y-8 mt-11 mb-12 max-h-96 overflow-y-auto',
	styleDATA,
	classNameBTNS = 'flex flex-row w-full',
	styleBTNS,
}) {
	/* Submmit data */
	const [data, setData] = React.useState();

	/* Icon hook */
	const { Spinner: SpinnerIcon } = useIcons(colors);

	React.useEffect(() => {
		setData(portal?.config?.popUps?.submitSummary);
	}, [portal?.config?.popUps]);

	/* Default values for the styles */
	const defaultStyleDIV = {
		backgroundColor: `${colors?.background}`,
		transition: 'all 100ms ease-in',
	};
	const defaultStyleSPAN = {
		color: `${colors?.primary}`
	};
	const defaultStyleDATA = {
	};
	const defaultStyleBTNS = {
	};

	/* This allows for only one of the fields to be modified and still keep the other constant values */
	const overrideStyleDIV = { ...defaultStyleDIV, ...styleDIV };
	const overrideStyleSPAN = { ...defaultStyleSPAN, ...styleSPAN };
	const overrideStyleDATA = { ...defaultStyleDATA, ...styleDATA };
	const overrideStyleBTNS = { ...defaultStyleBTNS, ...styleBTNS };

	const [shareholderData, setShareholderData] = React.useState();
	const [representativeData, setRepresentativeData] = React.useState();
	const [attachments, setAttachments] = React.useState();
	const [votes, setVotes] = React.useState();
	const [delegateData, setDelegateData] = React.useState();

	/*
	* All the getX functions are really verbose
	* but very similar, the only main diference between them
	* being which data they access the first time
	*/

	const getShareholderData = () => {
		const mixedData = [];

		const shareData = Object.entries(formData)
			.filter(field => SHAREHOLDER_DATA.includes(field[0]))
			.filter(field => !!field[1]);

		/* If no laguages, remove default from view */
		if (
			(
				formData.personOrEntity === 0 &&
				!portal?.config?.inputs?.[formType]?.person?.find(inp => inp.name === 'language')?.isActive
			)
			|| (
				formData.personOrEntity === 1 &&
				!portal?.config?.inputs?.[formType]?.entity?.find(inp => inp.name === 'language')?.isActive
			)
			|| !!formData.personOrEntity
		) {
			const index = shareData.indexOf(shareData.find(field => field[0] === 'language'));
			if (index > -1) {
				shareData.splice(index, 1);
			}
		}

		shareData.forEach(field => {
			const input = formData.personOrEntity ?
				portal?.config?.inputs?.[formType]?.entity?.find(inp => inp.name === field[0])
				: portal?.config?.inputs?.[formType]?.person?.find(inp => inp.name === field[0]);
			const label = getText(input, translate);
			let value;
			/* If it's an input with preset options */
			if (input?.items?.length > 0) {
				/* If it's a multianswer input */
				if (Array.isArray(field[1]) && field[1].length > 0) {
					value = [];
					field[1].forEach(key => {
						const item = input.items.find(it => it.value === key);
						value.push(getText(item, translate));
					});
					mixedData.push([label, value]);
					return;
				}
				/* If its a simple answer input */
				const item = input.items.find(it => it.value === field[1]);
				value = getText(item, translate);
				mixedData.push([label, value]);
				return;
			}
			/* If the value is not translatable */
			mixedData.push([label, field[1]]);
		});

		return mixedData;
	};

	const getRepresentativeData = () => {
		const mixedData = [];

		const repreData = Object.entries(formData?.representative)
			.filter(field => !!field[1]);

		/* If no laguages, remove default from view */
		if (!portal?.config?.inputs?.[formType]?.representative?.find(inp => inp.name === 'language')?.isActive) {
			const index = repreData.indexOf(repreData.find(field => field[0] === 'language'));
			if (index > -1) {
				repreData.splice(index, 1);
			}
		}

		repreData.forEach(field => {
			const input = portal?.config?.inputs?.[formType]?.representative?.find(inp => inp.name === field[0]);
			const label = getText(input, translate);
			let value;
			/* If it's an input with preset options */
			if (input?.items?.length > 0) {
				/* If it's a multianswer input */
				if (Array.isArray(field[1]) && field[1].length > 0) {
					value = [];
					field[1].forEach(key => {
						const item = input.items.find(it => it.value === key);
						value.push(getText(item, translate));
					});
					mixedData.push([label, value]);
					return;
				}
				/* If its a simple answer input */
				const item = input.items.find(it => it.value === field[1]);
				value = getText(item, translate);
				mixedData.push([label, value]);
				return;
			}
			/* If the value is not translatable */
			mixedData.push([label, field[1]]);
		});

		return mixedData;
	};

	function formatVote({
		voteId,
		voteValue,
		voteOrder
	}) {
		const agenda = portal?.council?.agendas?.find(agend => agend.id === voteId);
		const label = agenda?.agendaSubject;
		let value;

		/* Formats items from server structure to value, text, label structure */
		const formatCustomItems = items => [...items.map(item => ({ value: item.id, text: item.value, label: '' })), CUSTOM_ABSTENTION_AGENDAS];

		/* Custom agendas display can have simple or multiple answer */
		if (CUSTOM_AGENDAS.includes(agenda?.subjectType)) {
			/* We need to format the options for the inputs */
			const items = formatCustomItems(agenda.items);

			/* Only one answer */
			if (agenda.options.maxSelections === 1) {
				const item = items.find(ite => ite.value === voteValue);
				value = item.text === '' ? getText(item, translate) : item.text;
				return [label, value, voteOrder];
			}

			/* Multiple answers */
			value = [];
			voteValue.forEach(val => {
				const item = items.find(ite => ite.value === val);
				value.push(item.text);
			});
			return [label, value, voteOrder];
		}

		/* Confirmation agendas have only Yes or NO answers */
		if (agenda?.subjectType === CONFIRMATION_AGENDAS) {
			const item = CONFIRMATION_ITEMS_AGENDA?.find(ite => ite.value === voteValue);
			value = getText(item, translate);
			return [label, value, voteOrder];
		}

		/* Default agendas have Yes, No and Abstention */
		const item = DEFAULT_ITEMS_AGENDA?.find(ite => ite.value === voteValue);
		value = getText(item, translate);
		return [label, value, voteOrder];
	}

	const getVotes = () => {
		const mixedData = [];
		const votesArray = formData.votes
			.filter(vote => vote.value !== null
				&& vote.value !== undefined
				&& (
					(Array.isArray(vote.value) && vote.value.length > 0)
					|| Number.isInteger(vote.value)
				));

		votesArray.forEach(vote => {
			mixedData.push(formatVote({ voteId: vote.id, voteOrder: vote.orderIndex, voteValue: vote.value }));
			/* Works with the actual format of voting points because they start with a number according his position */
			mixedData.sort((a, b) => a[2] - b[2]);
		});

		return mixedData;
	};

	const getDelegationData = () => {
		const mixedData = [];

		const deleData = Object.entries(formData?.delegate)
			.filter(([key, value]) => key !== 'id' && !!value);

		deleData.forEach(field => {
			const input = portal?.config?.inputs?.[formType]?.delegate?.find(inp => inp.name === field[0]);
			const label = getText(field[0], translate);
			let value;
			/* If it's an input with preset options */
			if (input?.items?.length > 0) {
				/* If it's a multianswer input */
				if (Array.isArray(field[1]) && field[1].length > 0) {
					value = [];
					field[1].forEach(key => {
						const item = input.items.find(it => it.value === key);
						value.push(getText(item, translate));
					});
					mixedData.push([label, value]);
					return;
				}
				/* If its a simple answer input */
				const item = input.items.find(it => it.value === field[1]);
				value = getText(item, translate);
				mixedData.push([label, value]);
				return;
			}
			/* If the value is not translatable */
			mixedData.push([label, field[1]]);
		});

		return mixedData;
	};
	function isObjEmpty(obj) {
		let flag = true;
		Object.entries(obj).forEach(([key, value]) => {
			if (key !== 'language' && !!value) {
				flag = false;
			}
		});
		return flag;
	}
	React.useEffect(() => {
		setShareholderData(getShareholderData());
		setRepresentativeData(getRepresentativeData());
		setAttachments(Object.entries(formData.attachments)
			.map(field => [field[1]?.text, field[1]?.name]));
		setVotes(getVotes());
		setDelegateData(getDelegationData());
	}, [formData]);

	return <div
		className={classNameDIV}
		style={overrideStyleDIV}
	>
		{/* TITLE */}
		<span className={classNameTITLE} style={overrideStyleSPAN}>{getText(data?.title, translate)}</span>

		{/* DATA LIST */}
		<div className={classNameDATA} style={overrideStyleDATA}>
			{/* SHAREHOLDER DATA */}
			<DataBlock
				data={shareholderData}
				title={getText(data?.participantData, translate)}
				colors={colors}
			/>
			{/* REPRESENTATIVE DATA */}
			{!isObjEmpty(formData.representative)
				&& <DataBlock
					data={representativeData}
					title={getText(data?.representativeData, translate)}
					colors={colors}
				/>
			}
			{/* ATTACHMENTS */}
			{attachments?.length > 0
				&& <DataBlock
					data={attachments}
					title={getText(data?.attachments, translate)}
					colors={colors}
				/>
			}
			{/* DELEGATE */}
			{delegateData?.length > 0
				&& <DataBlock
					data={delegateData}
					title={getText(data?.delegation, translate)}
					colors={colors}
				/>
			}
			{/* VOTES */}
			{votes?.length > 0 && (formType === 'earlyVote' || (portal?.config?.optionalFields[formType]?.earlyVote === true && formData?.votesIsActive))
				&& <DataBlock
					data={votes}
					title={getText(data?.votes, translate)}
					colors={colors}
					classNameFIELD={'flex flex-col space-y-2'}
				/>
			}
			{/* VOTES */}
			{formData.intervention
				&& <DataBlock
					data={[['intervention', formData.intervention]]}
					title={getText(data?.interventions, translate)}
					showLabel={false}
					colors={colors}
					classNameFIELD={'flex flex-col space-y-2'}
				/>
			}
			{/* SEND BUTTON */}
			{isMobile &&
				<div style={overrideStyleBTNS}>
					{/* SEND */}
					<SimpleButton
						colors={colors}
						disabled={sending}
						classNameBTN='px-3 cursor-pointer focus:outline-none text-sm bold rounded-md w-full h-10'
						tag={getText(data?.confirmButton, translate)}
						loading={sending}
						onClick={() => onConfirm()}
					/>
					{/* CANCEL */}
					{!sending
						&& <div
							className='px-7 py-4 w-full text-center text-sm font-bold cursor-pointer h-10'
						>
							<span
								style={overrideStyleSPAN}
								onClick={() => setOpen(false)}
							>
								{getText(data?.cancelButton, translate)}
							</span>
						</div>
					}
					{/* SPINNER */}
					{sending
						&& <div className='flex row w-max px-8 space-x-4 items-center'>
							<Spinner Icon={SpinnerIcon} />
							<span style={overrideStyleSPAN}>{getText(data?.sendingText, translate)}</span>
						</div>}
				</div>
			}
		</div>
		{!isMobile &&
			<div className={classNameBTNS} style={overrideStyleBTNS}>
				{/* SEND */}
				<SimpleButton
					colors={colors}
					disabled={sending}
					classNameBTN='px-3 cursor-pointer focus:outline-none text-sm md:text-lg bold rounded-md w-max pd-10'
					tag={getText(data?.confirmButton, translate)}
					loading={sending}
					onClick={() => onConfirm()}
				/>
				{/* CANCEL */}
				{!sending
					&& <div
						className='px-7 py-4 w-max text-sm md:text-xl font-bold cursor-pointer'
					>
						<span
							style={overrideStyleSPAN}
							onClick={() => setOpen(false)}
						>
							{getText(data?.cancelButton, translate)}
						</span>
					</div>
				}
				{/* SPINNER */}
				{sending
					&& <div className='flex row w-max px-8 space-x-4 items-center'>
						<Spinner Icon={SpinnerIcon} />
						<span style={overrideStyleSPAN}>{getText(data?.sendingText, translate)}</span>
					</div>}
			</div>
		}
	</div>;
}

export default Summary;

function DataBlock({
	data,
	colors,
	title,

	/* OPTIONAL PROPS */
	showTitle = true,
	showLabel = true,
	classNameDIV = 'space-y-5',
	styleDIV,
	classNameTITLE = 'self-center font-semibold md:text-lg',
	classNameFIELD = 'flex space-x-6',
	classNameSPAN = 'text-sm md:text-base',
	styleSPAN,
	classNameVALUE = 'text-sm md:text-base',
	styleVALUE,
}) {
	/* Default values for the styles */
	const defaultStyleDIV = {
	};
	const defaultStyleSPAN = {
		color: `${colors?.primary}`
	};
	const defaultStyleVALUE = {
		color: 'black'
	};

	/* This allows for only one of the fields to be modified and still keep the other constant values */
	const overrideStyleDIV = { ...defaultStyleDIV, ...styleDIV };
	const overrideStyleSPAN = { ...defaultStyleSPAN, ...styleSPAN };
	const overrideStyleVALUE = { ...defaultStyleVALUE, ...styleVALUE };

	return <div className={classNameDIV} style={overrideStyleDIV}>
		{showTitle && <span className={classNameTITLE} style={overrideStyleSPAN} >{title}</span>}
		{data?.map(field => (
			<div key={field[0]} className={classNameFIELD}>
				{showLabel && <span
					style={overrideStyleSPAN}
					className={classNameSPAN}
				>
					{field[0]}
				</span>}
				<span
					style={overrideStyleVALUE}
					className={classNameVALUE}
				>
					{Array.isArray(field[1]) ? field[1].join(', ') : field[1]}
				</span>
			</div>
		))}
	</div>;
}
